/**
 * Geohash decoding utility.
 * Base32 alphabet: 0123456789bcdefghjkmnpqrstuvwxyz
 */

const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Decode a geohash string to the center latitude/longitude of the cell.
 */
export function decodeGeohash(geohash: string): LatLng {
  let isLng = true;
  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;

  for (const char of geohash.toLowerCase()) {
    const idx = BASE32.indexOf(char);
    if (idx === -1) continue;

    for (let bit = 4; bit >= 0; bit--) {
      const bitValue = (idx >> bit) & 1;

      if (isLng) {
        const mid = (lngMin + lngMax) / 2;
        if (bitValue === 1) {
          lngMin = mid;
        } else {
          lngMax = mid;
        }
      } else {
        const mid = (latMin + latMax) / 2;
        if (bitValue === 1) {
          latMin = mid;
        } else {
          latMax = mid;
        }
      }

      isLng = !isLng;
    }
  }

  return {
    lat: (latMin + latMax) / 2,
    lng: (lngMin + lngMax) / 2,
  };
}

/**
 * Check if a string looks like a valid geohash (1-12 chars, base32 alphabet).
 */
export function isValidGeohash(str: string): boolean {
  if (str.length < 1 || str.length > 12) return false;
  return [...str.toLowerCase()].every(c => BASE32.includes(c));
}

/**
 * Encode a latitude/longitude into a geohash of the given precision.
 * Default precision 6 (~1.2km) suits city-level merchant placement.
 */
export function encodeGeohash(lat: number, lng: number, precision = 6): string {
  let isLng = true;
  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;
  let bit = 0;
  let idx = 0;
  let geohash = '';

  while (geohash.length < precision) {
    if (isLng) {
      const mid = (lngMin + lngMax) / 2;
      if (lng >= mid) {
        idx = idx * 2 + 1;
        lngMin = mid;
      } else {
        idx = idx * 2;
        lngMax = mid;
      }
    } else {
      const mid = (latMin + latMax) / 2;
      if (lat >= mid) {
        idx = idx * 2 + 1;
        latMin = mid;
      } else {
        idx = idx * 2;
        latMax = mid;
      }
    }
    isLng = !isLng;

    if (bit < 4) {
      bit++;
    } else {
      geohash += BASE32[idx];
      bit = 0;
      idx = 0;
    }
  }

  return geohash;
}
